import { useMemo, useState } from 'react';
import { LessonHeader } from './LessonHeader';
import { CheckIcon, CrossIcon, primaryBtn, screenShell } from './common';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { useAsync } from '../../hooks/useAsync';
import { contentApi } from '../../api/content';
import type { LessonScreenProps } from './types';

function shuffle<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

export function KanaQuizScreen({ lessonId, unitName, lessonName, onBack, onNext }: LessonScreenProps) {
  const lesson = useAsync(() => contentApi.getLesson(lessonId), [lessonId]);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);

  const items = useMemo(() => (lesson.data ? lesson.data.items.filter((it) => it.itemType === 'KANA') : []), [lesson.data]);
  const item = items[index];

  const options = useMemo(() => {
    if (!item || item.itemType !== 'KANA') return [];
    const pool = items.filter((it) => it.itemType === 'KANA' && it.id !== item.id).map((it) => (it.itemType === 'KANA' ? it.romaji ?? '' : ''));
    const distractors = shuffle(pool.filter(Boolean)).slice(0, 3);
    return shuffle([item.romaji ?? '', ...distractors]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  if (lesson.loading) return <LoadingState />;
  if (lesson.error || !lesson.data) return <ErrorState message="Không tải được bài học." />;
  if (!item || item.itemType !== 'KANA') return <ErrorState message="Bài học này chưa có nội dung Kana." />;

  const isCorrect = picked === item.romaji;

  const next = () => {
    if (index < items.length - 1) {
      setIndex(index + 1);
      setPicked(null);
    } else {
      onNext();
    }
  };

  return (
    <div style={screenShell}>
      <LessonHeader onBack={onBack} ctx={{ track: 'kana', unit: unitName, lesson: lessonName, step: 2, total: 2 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24 }}>
        <div style={{ fontFamily: 'var(--font-jp)', fontSize: 96, fontWeight: 400, lineHeight: 1, color: 'var(--ink)' }}>{item.character}</div>
        {picked && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: isCorrect ? 'var(--success)' : 'var(--error)',
              color: '#fff',
              borderRadius: 999,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {isCorrect ? <CheckIcon /> : <CrossIcon />}
            {isCorrect ? 'Đúng rồi!' : 'Chưa đúng'}
          </div>
        )}
      </div>

      <div style={{ padding: '0 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {options.map((opt) => {
            const isThisCorrect = opt === item.romaji;
            let bg = 'var(--surface-page)';
            let border = 'var(--border-default)';
            if (picked && isThisCorrect) {
              bg = 'rgba(46,139,87,0.12)';
              border = 'var(--success)';
            } else if (picked && opt === picked && !isCorrect) {
              bg = 'var(--error-bg)';
              border = 'var(--error)';
            }
            return (
              <button
                key={opt}
                onClick={() => !picked && setPicked(opt)}
                style={{
                  height: 84,
                  borderRadius: 'var(--radius-card)',
                  border: `2px solid ${border}`,
                  background: bg,
                  cursor: picked ? 'default' : 'pointer',
                  fontFamily: 'var(--font-vi)',
                  fontSize: 22,
                  fontWeight: 600,
                  color: 'var(--ink)',
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ padding: 20, borderTop: '1px solid var(--border-default)', marginTop: 20 }}>
        <button onClick={next} disabled={!picked} style={{ ...primaryBtn, opacity: picked ? 1 : 0.45, cursor: picked ? 'pointer' : 'not-allowed' }}>
          Tiếp theo
        </button>
      </div>
    </div>
  );
}
